import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  PageHeader,
  SearchInput,
  Select,
} from '@acs/ui';
import type { Column } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import type { ClientDoc } from '../../shared/api/converters.js';
import { formatShortDate } from '../../shared/lib/format.js';
import { AddClientDialog } from './AddClientDialog.js';
import { DEFAULT_SORT, SORT_MODES, filterAndSort, type SortMode } from './clientSearch.js';
import { useClients } from './hooks.js';
import { clientPath } from './routes.js';

/** Cap on rendered rows (DOM-size bound, doc 12 §7). Search always filters the
 * FULL loaded list, so nothing is unreachable. */
const RENDER_CAP = 200;

const SORT_STORE_KEY = (businessId: string) => `tc24.clientsSort.${businessId}`;

/**
 * The client base (design template — Portal · Clients): everyone the AI has
 * spoken with, saved automatically, searchable by name or phone.
 */
export function ClientsListPage() {
  const { t, tag } = useI18n();
  const business = useActiveBusiness();
  const navigate = useNavigate();
  const { data, isLoading } = useClients(business.id);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortMode>(
    () => (localStorage.getItem(SORT_STORE_KEY(business.id)) as SortMode) || DEFAULT_SORT,
  );
  const [adding, setAdding] = useState(false);

  const all = data ?? [];
  const locale = tag;
  const filtered = useMemo(() => filterAndSort(all, query, sort, locale), [all, query, sort, locale]);
  const shown = filtered.slice(0, RENDER_CAP);
  const searching = query.trim().length > 0;

  const setSortPersisted = (mode: SortMode) => {
    setSort(mode);
    localStorage.setItem(SORT_STORE_KEY(business.id), mode);
  };

  const columns = clientColumns(business.timezone, tag, t);

  return (
    <>
      <PageHeader
        title={t('clients.title')}
        subtitle={t('clients.subtitle')}
        actions={
          <Button leadingIcon={<Icon name="plus" size={16} />} onClick={() => setAdding(true)}>
            {t('clients.add')}
          </Button>
        }
      />

      <div className="calls-filter">
        <div style={{ flex: '1 1 260px', minWidth: 200 }}>
          <SearchInput
            clearLabel={t('ui.clearSearch')}
            value={query}
            onValueChange={setQuery}
            onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
            placeholder={t('clients.searchPlaceholder')}
            aria-label={t('clients.searchLabel')}
          />
        </div>
        <Select
          aria-label={t('clients.sortLabel')}
          value={sort}
          onChange={(e) => setSortPersisted(e.target.value as SortMode)}
        >
          {SORT_MODES.map((m) => (
            <option key={m} value={m}>
              {t(`clients.sort.${m}` as TranslationKey)}
            </option>
          ))}
        </Select>
      </div>

      <p className="visually-hidden" aria-live="polite">
        {searching ? t('clients.shownCount', { count: filtered.length }) : ''}
      </p>

      <DataTable
        columns={columns}
        rows={shown}
        rowKey={(c) => c.id}
        loading={isLoading}
        caption={t('clients.caption')}
        onRowClick={(c) => navigate(clientPath(business.id, c.id))}
        renderCard={(c) => <ClientCard client={c} timezone={business.timezone} locale={tag} />}
        empty={
          all.length === 0 ? (
            <EmptyState
              icon={<Icon name="users" size={24} />}
              title={t('clients.empty.title')}
              description={t('clients.empty.description')}
              actions={
                <Button leadingIcon={<Icon name="plus" size={16} />} onClick={() => setAdding(true)}>
                  {t('clients.add')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<Icon name="search" size={24} />}
              iconTone="neutral"
              title={t('clients.noMatch.title', { query: query.trim() })}
              description={t('clients.noMatch.description')}
              actions={
                <Button variant="secondary" onClick={() => setQuery('')}>
                  {t('clients.clearSearch')}
                </Button>
              }
            />
          )
        }
      />

      <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
        {searching ? `${filtered.length} of ${all.length} clients` : `${all.length} client${all.length === 1 ? '' : 's'}`}
        {filtered.length > RENDER_CAP && ` — showing the first ${RENDER_CAP}, search to narrow down.`}
      </p>

      {adding && (
        <AddClientDialog
          businessId={business.id}
          country={business.country}
          onClose={() => setAdding(false)}
          onOpenExisting={(e164) => {
            setAdding(false);
            navigate(clientPath(business.id, e164));
          }}
        />
      )}
    </>
  );
}

function clientColumns(
  timezone: string,
  locale: string,
  t: (key: TranslationKey) => string,
): Column<ClientDoc>[] {
  return [
    {
      key: 'name',
      header: t('clients.column.client'),
      sortable: true,
      sortValue: (c) => c.name.toLowerCase(),
      render: (c) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={c.name} size="sm" />
          <span>
            <span style={{ display: 'block', fontWeight: 600 }}>{c.name}</span>
            {c.source === 'manual' && (
              <span className="muted" style={{ fontSize: 11 }}>{t('clients.addedManually')}</span>
            )}
          </span>
        </span>
      ),
    },
    { key: 'phone', header: t('clients.column.phone'), cellClassName: 'mono', render: (c) => c.phone },
    {
      key: 'bookings',
      header: t('clients.column.bookings'),
      sortable: true,
      align: 'end',
      sortValue: (c) => c.bookingsCount,
      render: (c) => (c.bookingsCount > 0 ? c.bookingsCount : '—'),
    },
    { key: 'service', header: t('clients.column.topService'), render: (c) => c.recentServices[0]?.serviceName ?? '—' },
    {
      key: 'last',
      header: t('clients.column.lastSeen'),
      sortable: true,
      sortValue: (c) => c.lastSeenAt?.getTime() ?? 0,
      render: (c) => (c.lastSeenAt ? formatShortDate(c.lastSeenAt, timezone, locale) : '—'),
    },
  ];
}

function ClientCard({ client, timezone, locale }: { client: ClientDoc; timezone: string; locale: string }) {
  const { t } = useI18n();
  return (
    <>
      <div className="tc-cardlist__row">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={client.name} size="sm" />
          <span style={{ fontWeight: 600 }}>{client.name}</span>
        </span>
        {client.bookingsCount > 0 && (
          <Badge tone="neutral" size="sm">
            {t('clients.bookingsBadge', { count: client.bookingsCount })}
          </Badge>
        )}
      </div>
      <div className="tc-cardlist__meta">
        {client.phone}
        {client.lastSeenAt ? ` · ${formatShortDate(client.lastSeenAt, timezone, locale)}` : ''}
      </div>
    </>
  );
}
